import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Search, Copy, Check, RotateCw } from "lucide-react";
import { Loading } from "./common/Loading";

interface TraceLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  traceLogs: string[] | null;
  isLoading?: boolean;
  /** Перезапустить запрос с trace и обновить логи */
  onRerun?: () => void;
}

// Функция для парсинга и подсветки логов
// Формат: 2025-12-11 22:25:11,202 INFO     clickhouse_driver.log: message
// Или: 2025-12-11 22:41:23,514 DEBUG clickhouse_driver.connection: Query: ...
const parseLogLine = (line: string, searchTerm: string = "") => {
  // Регулярное выражение для парсинга лога с миллисекундами через запятую
  // Формат: YYYY-MM-DD HH:MM:SS,mmm LEVEL     module: message
  // Модуль обычно содержит буквы, цифры, точки, подчеркивания и заканчивается на :
  // Используем более точный паттерн для модуля: [a-zA-Z0-9_.]+: (явно указываем все допустимые символы)
  // Делаем пробелы после уровня более гибкими
  const logRegex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})\s+(\w+)\s+([a-zA-Z0-9_.]+):\s*(.*)$/;
  const match = line.match(logRegex);
  
  if (match) {
    const [, dateTime, level, module, message] = match;
    const upperLevel = level.trim().toUpperCase();
    
    // Проверяем, является ли это уровнем лога
    const isLogLevel = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'TRACE'].includes(upperLevel);
    
    if (isLogLevel) {
      // Извлекаем только время из даты и времени (формат: YYYY-MM-DD HH:MM:SS,mmm -> HH:MM:SS,mmm)
      const timeOnly = dateTime.includes(' ') ? dateTime.split(' ')[1] : dateTime;
      return {
        dateTime: timeOnly,
        level: upperLevel,
        module: "",
        message,
        fullLine: line,
      };
    }
  }
  
  // Пробуем более простой формат: дата время уровень (любые символы до первого двоеточия с пробелом после)
  // Это для случаев, когда модуль может содержать другие символы
  const logRegexSimple = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})\s+(DEBUG|INFO|WARNING|ERROR|TRACE)\s+([^:]+?):\s+(.*)$/i;
  const matchSimple = line.match(logRegexSimple);
  
  if (matchSimple) {
    const [, dateTime, level, module, message] = matchSimple;
    const upperLevel = level.trim().toUpperCase();
    
    // Извлекаем только время из даты и времени
    const timeOnly = dateTime.includes(' ') ? dateTime.split(' ')[1] : dateTime;
    return {
      dateTime: timeOnly,
      level: upperLevel,
      module: "",
      message,
      fullLine: line,
    };
  }
  
  // Пробуем формат без миллисекунд (точка вместо запятой)
  const logRegexNoMs = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?)\s+(\w+)\s+([a-zA-Z0-9_.]+):\s*(.*)$/;
  const matchNoMs = line.match(logRegexNoMs);
  
  if (matchNoMs) {
    const [, dateTime, level, module, message] = matchNoMs;
    const upperLevel = level.trim().toUpperCase();
    const isLogLevel = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'TRACE'].includes(upperLevel);
    
    // Извлекаем только время из даты и времени
    const timeOnly = dateTime.includes(' ') ? dateTime.split(' ')[1] : dateTime;
    return {
      dateTime: timeOnly,
      level: isLogLevel ? upperLevel : "",
      module: "",
      message,
      fullLine: line,
    };
  }
  
  // Пробуем формат без модуля (без двоеточия после уровня)
  // Ищем паттерн: дата время уровень (пробелы) остальное
  const logRegexNoModule = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:,\d{3}|\.\d+)?)\s+(\w+)\s+(.*)$/;
  const matchNoModule = line.match(logRegexNoModule);
  
  if (matchNoModule) {
    const [, dateTime, level, rest] = matchNoModule;
    const upperLevel = level.trim().toUpperCase();
    const isLogLevel = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'TRACE'].includes(upperLevel);
    
    // Извлекаем только время из даты и времени
    const timeOnly = dateTime.includes(' ') ? dateTime.split(' ')[1] : dateTime;
    return {
      dateTime: timeOnly,
      level: isLogLevel ? upperLevel : "",
      module: "",
      message: rest,
      fullLine: line,
    };
  }
  
  // Если не соответствует формату, просто возвращаем как есть
  return {
    dateTime: "",
    level: "",
    module: "",
    message: line,
    fullLine: line,
  };
};

// SQL ключевые слова для подсветки
const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER',
  'ON', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'LIKE', 'ILIKE', 'BETWEEN', 'IS', 'NULL',
  'GROUP', 'BY', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'AS', 'CASE', 'WHEN',
  'THEN', 'ELSE', 'END', 'UNION', 'ALL', 'INTERSECT', 'EXCEPT', 'INSERT', 'INTO', 'VALUES',
  'UPDATE', 'SET', 'DELETE', 'CREATE', 'TABLE', 'DROP', 'ALTER', 'INDEX', 'VIEW', 'TRUNCATE',
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'CONSTRAINT', 'UNIQUE', 'CHECK', 'DEFAULT',
  'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'CAST', 'CONVERT', 'COALESCE', 'NULLIF', 'IFNULL',
  'SUBSTRING', 'CONCAT', 'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'REPLACE', 'DATE', 'TIME',
  'DATETIME', 'TIMESTAMP', 'YEAR', 'MONTH', 'DAY', 'HOUR', 'MINUTE', 'SECOND', 'NOW', 'TODAY',
  'INT', 'BIGINT', 'SMALLINT', 'TINYINT', 'FLOAT', 'DOUBLE', 'DECIMAL', 'NUMERIC', 'VARCHAR',
  'CHAR', 'TEXT', 'BLOB', 'BOOLEAN', 'BOOL', 'JSON', 'ARRAY', 'MAP', 'STRUCT', 'UNION'
];

// Функция для подсветки синтаксиса в тексте (SQL, числа, строки)
const highlightSyntax = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Создаем единый паттерн для всех токенов с приоритетами
  const tokenPatterns: Array<{ pattern: RegExp; className: string }> = [
    // Строки в одинарных кавычках (высший приоритет)
    { pattern: /'([^'\\]|\\.)*'/g, className: 'text-yellow-300' },
    // Строки в двойных кавычках
    { pattern: /"([^"\\]|\\.)*"/g, className: 'text-yellow-300' },
    // Имена в обратных кавычках
    { pattern: /`[^`]+`/g, className: 'text-purple-400' },
    // SQL ключевые слова
    { pattern: new RegExp(`\\b(${SQL_KEYWORDS.join('|')})\\b`, 'gi'), className: 'text-blue-400 font-semibold' },
    // Числа (целые и с плавающей точкой)
    { pattern: /\b\d+\.?\d*\b/g, className: 'text-cyan-400' },
    // Операторы сравнения
    { pattern: /(>=|<=|<>|!=|==|=|>|<)/g, className: 'text-red-400' },
    // Логические операторы
    { pattern: /\b(AND|OR|NOT)\b/gi, className: 'text-pink-400 font-semibold' },
  ];
  
  // Собираем все совпадения с их позициями
  interface TokenMatch {
    start: number;
    end: number;
    text: string;
    className: string;
  }
  
  const tokens: TokenMatch[] = [];
  
  tokenPatterns.forEach(({ pattern, className }) => {
    const matches = Array.from(text.matchAll(pattern));
    matches.forEach(match => {
      if (match.index !== undefined) {
        tokens.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          className,
        });
      }
    });
  });
  
  // Сортируем токены по позиции и удаляем перекрывающиеся (приоритет первым найденным)
  tokens.sort((a, b) => a.start - b.start);
  const filteredTokens: TokenMatch[] = [];
  let lastEnd = 0;
  
  tokens.forEach(token => {
    if (token.start >= lastEnd) {
      filteredTokens.push(token);
      lastEnd = token.end;
    }
  });
  
  // Разбиваем текст на части
  const parts: Array<{ text: string; className?: string }> = [];
  let currentIndex = 0;
  
  filteredTokens.forEach(token => {
    // Текст до токена
    if (token.start > currentIndex) {
      parts.push({ text: text.substring(currentIndex, token.start) });
    }
    // Сам токен
    parts.push({ text: token.text, className: token.className });
    currentIndex = token.end;
  });
  
  // Остаток текста
  if (currentIndex < text.length) {
    parts.push({ text: text.substring(currentIndex) });
  }
  
  if (parts.length === 0) {
    parts.push({ text });
  }
  
  return (
    <>
      {parts.map((part, index) => (
        <span key={index} className={part.className || 'text-gray-300'}>
          {part.text}
        </span>
      ))}
    </>
  );
};

// Функция для подсветки найденного текста
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm) {
    // Если нет поиска, применяем только подсветку синтаксиса
    return highlightSyntax(text);
  }
  
  // Если есть поиск, сначала находим совпадения, потом подсвечиваем синтаксис
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearch})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, index) => {
        // Проверяем, является ли часть совпадением (через сравнение без учета регистра)
        const isMatch = part.toLowerCase() === searchTerm.toLowerCase();
        if (isMatch) {
          return (
            <mark key={index} className="bg-yellow-300 text-black font-semibold">
              {part}
            </mark>
          );
        } else {
          // Для несовпадающих частей применяем подсветку синтаксиса
          return <React.Fragment key={index}>{highlightSyntax(part)}</React.Fragment>;
        }
      })}
    </>
  );
};

// Цвета для уровней логов (яркие и заметные с фоном)
const getLevelColor = (level: string): { text: string; bg: string } => {
  const upperLevel = level.toUpperCase();
  switch (upperLevel) {
    case "DEBUG":
      return { text: "text-gray-200", bg: "bg-gray-700" };
    case "INFO":
      return { text: "text-blue-100", bg: "bg-blue-600" };
    case "WARNING":
      return { text: "text-yellow-100", bg: "bg-yellow-600" };
    case "ERROR":
      return { text: "text-red-100", bg: "bg-red-600" };
    case "TRACE":
      return { text: "text-purple-100", bg: "bg-purple-600" };
    default:
      return { text: "text-gray-200", bg: "bg-gray-700" };
  }
};

export const TraceLogsModal = ({ isOpen, onClose, traceLogs, isLoading = false, onRerun }: TraceLogsModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Фильтрация логов по поисковому запросу
  const filteredLogs = useMemo(() => {
    if (!traceLogs) return [];
    if (!searchTerm) return traceLogs;
    
    const lowerSearch = searchTerm.toLowerCase();
    return traceLogs.filter(log => log.toLowerCase().includes(lowerSearch));
  }, [traceLogs, searchTerm]);

  // Обработчик клика по операции - добавляет или удаляет из поиска
  const handleOperationClick = (operation: string) => {
    const lowerOperation = operation.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    
    // Проверяем, есть ли операция в поиске (с учетом границ слов)
    const operationRegex = new RegExp(`\\b${lowerOperation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    const isInSearch = operationRegex.test(searchTerm);
    
    if (isInSearch) {
      // Если операция уже есть, удаляем её
      // Разбиваем поиск на слова, удаляем операцию и собираем обратно
      const words = searchTerm.split(/\s+/).filter(word => 
        word.toLowerCase() !== lowerOperation
      );
      setSearchTerm(words.join(' ').trim());
    } else {
      // Если операции нет, добавляем её
      if (searchTerm) {
        setSearchTerm(`${searchTerm} ${operation}`.trim());
      } else {
        setSearchTerm(operation);
      }
    }
    // Фокусируемся на поле поиска
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  // При открытии модального окна скроллим вверх
  useEffect(() => {
    if (logsContainerRef.current && isOpen) {
      logsContainerRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Фокус на поиск при открытии
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Копирование всех логов
  const handleCopyAll = async () => {
    if (!traceLogs) return;
    
    const text = traceLogs.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Скролл вверх
  const scrollToTop = () => {
    logsContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Скролл вниз
  const scrollToBottom = () => {
    logsContainerRef.current?.scrollTo({ top: logsContainerRef.current.scrollHeight, behavior: "smooth" });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-7xl h-[85vh] flex flex-col m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-white">Debug Logs</h2>
            {traceLogs && (
              <span className="text-sm text-gray-400">
                {filteredLogs.length} / {traceLogs.length} строк
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onRerun && (
              <button
                onClick={onRerun}
                disabled={isLoading}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                title="Перезапустить запрос и обновить логи"
                aria-label="Перезапустить запрос"
              >
                <RotateCw size={18} className={isLoading ? "animate-spin" : ""} />
              </button>
            )}
            <button
              onClick={handleCopyAll}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              title="Копировать все логи"
              aria-label={copied ? "Скопировано" : "Копировать"}
            >
              {copied ? (
                <Check size={18} className="text-green-400" />
              ) : (
                <Copy size={18} />
              )}
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              title="Закрыть"
              aria-label="Закрыть"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Поиск и фильтры */}
        <div className="p-4 border-b border-gray-700 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Поиск по логам..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>
          
          {/* Фильтры по операциям */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-400 self-center">Операции:</span>
            {["Reading", "Filter", "Expression", "Aggregation", "Merge", "Writing"].map(operation => {
              const isInSearch = searchTerm.toLowerCase().includes(operation.toLowerCase());
              return (
                <button
                  key={operation}
                  onClick={() => handleOperationClick(operation)}
                  className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                    isInSearch
                      ? "bg-blue-600 text-blue-100"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                  }`}
                  title="Добавить в поиск"
                >
                  {operation}
                </button>
              );
            })}
          </div>
        </div>

        {/* Логи */}
        <div
          ref={logsContainerRef}
          className="flex-1 overflow-auto p-4 font-mono text-sm relative"
          style={{ backgroundColor: "#1e1e1e" }}
        >
          {isLoading ? (
            <div className="absolute inset-0 bg-gray-900/90 z-10 flex items-center justify-center">
              <Loading message="Выполнение запроса..." />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              {searchTerm ? "Ничего не найдено" : "Нет логов для отображения"}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredLogs.map((log, index) => {
                const parsed = parseLogLine(log, searchTerm);
                const levelStyle = parsed.level ? getLevelColor(parsed.level) : null;
                
                return (
                  <div
                    key={index}
                    className="flex items-start gap-2 py-0.5 px-2 rounded transition-colors hover:bg-gray-800/70"
                  >
                    {parsed.dateTime && (
                      <span className="text-gray-500 flex-shrink-0">{parsed.dateTime}</span>
                    )}
                    {parsed.level && levelStyle && (
                      <span className={`${levelStyle.text} ${levelStyle.bg} flex-shrink-0 min-w-[60px] text-xs font-bold px-2 py-0.5 rounded uppercase text-center inline-flex items-center justify-center`}>
                        {parsed.level}
                      </span>
                    )}
                    <span className="text-gray-300 flex-1 break-words">
                      {highlightText(parsed.message || parsed.fullLine, searchTerm)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Футер: навигация по логам */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            {searchTerm && filteredLogs.length > 0 && (
              <span>Найдено: {filteredLogs.length} совпадений</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={scrollToTop}
              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm min-h-[44px]"
            >
              ↑ Вверх
            </button>
            <button
              onClick={scrollToBottom}
              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm min-h-[44px]"
            >
              ↓ Вниз
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};



